"use strict";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result) __defProp(target, key, result);
  return result;
};
import { LitElement, html } from "lit";
import { state, query } from "lit/decorators.js";
import { STCComponentMixin } from "../../../common/mixins/stc_component_mixin";
const FORM_ID = "eab-enhance-ai-form";
const JOB_TIMEOUT_MS = 30 * 60 * 1e3;
function notifyEnhanceUser(payload) {
  const win = window;
  if (win.HIP?.fn?.notify) {
    win.HIP.fn.notify(payload);
    return;
  }
  console.warn("[MicrositeEnhanceAiDialog] notify unavailable:", payload.message);
}
function parsePositiveInt(raw) {
  if (raw === null || raw === void 0) return null;
  const n = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}
function readMicrositeId() {
  const bootEl = document.getElementById("eab-microsite-boot-data");
  if (!bootEl?.textContent) return null;
  try {
    const j = JSON.parse(bootEl.textContent);
    return parsePositiveInt(j.microsite_id);
  } catch {
    return null;
  }
}
function getCsrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content ?? "";
}
function pageToFileName(page) {
  if (page.is_home_page) return "index.html";
  const slug = page.slug ?? page.name ?? String(page.id);
  return `${slug}.html`;
}
const ENHANCE_DIALOG_TAG = "stc-microsite-enhance-ai-dialog";
export function openEnhanceAiDialog(pages) {
  let el = document.querySelector(ENHANCE_DIALOG_TAG);
  if (!el) {
    el = document.createElement(ENHANCE_DIALOG_TAG);
    document.body.appendChild(el);
  }
  el.openDialog(pages);
}
export class MicrositeEnhanceAiDialog extends STCComponentMixin(LitElement) {
  constructor() {
    super(...arguments);
    this._opened = false;
    this._pages = [];
    this._selectedPageTitles = [];
    this._prompt = "";
    this._submitting = false;
  }
  // ── Public API ──────────────────────────────────────────────────────────────
  openDialog(pages) {
    this._pages = pages;
    this._selectedPageTitles = [];
    this._prompt = "";
    this._submitting = false;
    this._opened = true;
  }
  // ── Event handlers ──────────────────────────────────────────────────────────
  _handleDialogClose() {
    if (this._submitting) return;
    this._opened = false;
  }
  get _comboboxInput() {
    return this._pages.map((p) => ({ value: p.title, title: p.title }));
  }
  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (changedProperties.has("_opened") && this._opened && this._comboboxEl) {
      this._comboboxEl.values = this._selectedPageTitles;
    }
  }
  _syncSelectedPageTitlesFromCombobox(el) {
    const target = el ?? this._comboboxEl;
    const rawValues = target?.values;
    const vals = Array.isArray(rawValues) ? rawValues : [];
    this._selectedPageTitles = vals.filter((v) => typeof v === "string" && v.trim().length > 0);
  }
  _handlePageComboboxCommit(e) {
    this._syncSelectedPageTitlesFromCombobox(e.currentTarget);
  }
  _handlePromptInput(e) {
    const path = e.composedPath();
    const inner = path.find((n) => n instanceof HTMLTextAreaElement);
    const host = e.currentTarget;
    if (inner) {
      this._prompt = inner.value;
    } else {
      const v = host?.value;
      this._prompt = typeof v === "string" ? v : v != null ? String(v) : "";
    }
  }
  _handleFormSubmit(_e) {
    if (this._submitting) return;
    this._syncSelectedPageTitlesFromCombobox();
    void this._processPages();
  }
  _handleFormCancel() {
    if (this._submitting) return;
    this._opened = false;
  }
  // ── Processing ──────────────────────────────────────────────────────────────
  async _processPages() {
    const micrositeId = readMicrositeId();
    if (micrositeId == null) {
      notifyEnhanceUser({
        message: "Microsite context is missing. Reload the page and try again.",
        status: "error"
      });
      return;
    }
    const channel = window.user_notifications;
    if (!channel) {
      notifyEnhanceUser({
        message: "Real-time channel unavailable. Reload the page and try again.",
        status: "error"
      });
      return;
    }
    this._submitting = true;
    const pagesCtx = this._allPagesContext();
    const pageIdToZipPath = new Map(
      Object.entries(pagesCtx.fileToPageId).map(([zipPath, pageId]) => [pageId, zipPath])
    );
    const titleToPage = new Map(this._pages.map((p) => [p.title, p]));
    const resolvedFileNames = this._selectedPageTitles.map((title) => {
      const page = titleToPage.get(title);
      if (!page) return title;
      return pageIdToZipPath.get(page.id) ?? pageToFileName(page);
    });
    const jobId = await this._submitJob(micrositeId, resolvedFileNames, pagesCtx.html);
    if (jobId == null) {
      this._submitting = false;
      return;
    }
    const resultPages = await this._waitForJobResult(jobId, channel);
    if (resultPages == null) {
      this._submitting = false;
      return;
    }
    const fileToPageId = new Map(
      this._pages.map((p) => [pageToFileName(p), p.id])
    );
    for (const [fileName, pageId] of Object.entries(pagesCtx.fileToPageId)) {
      fileToPageId.set(fileName, pageId);
    }
    let successCount = 0;
    let errorCount = 0;
    for (const [fileName, html2] of Object.entries(resultPages)) {
      const pageId = fileToPageId.get(fileName);
      if (pageId != null && typeof html2 === "string" && html2.trim()) {
        window.dispatchEvent(
          new CustomEvent("microsite:ai-enhance-result", {
            detail: { page_id: pageId, output_mode: "html", content: html2, file_name: fileName }
          })
        );
        successCount++;
      } else {
        errorCount++;
      }
    }
    this._submitting = false;
    if (errorCount === 0) {
      notifyEnhanceUser({ message: `${successCount} page(s) enhanced successfully.`, status: "success" });
      this._opened = false;
    } else if (successCount > 0) {
      notifyEnhanceUser({
        message: `${successCount} page(s) enhanced. ${errorCount} failed \u2014 see status above.`,
        status: "error"
      });
    } else {
      notifyEnhanceUser({
        message: "Enhancement could not be applied. Your pages were not changed.",
        status: "error"
      });
    }
  }
  // Returns all pages' current HTML (keyed by actual zip path) plus an accurate file→page-ID
  // map. Uses window.__eabMicrositeGetAllPagesHtml registered by microsite_html_builder.tsx,
  // which reads htmlZipPath (eab_html_preview_zip_path) from the React cache — not the slug.
  _allPagesContext() {
    const win = window;
    if (typeof win.__eabMicrositeGetAllPagesHtml === "function") {
      const raw = win.__eabMicrositeGetAllPagesHtml();
      if (raw && "html" in raw && "fileToPageId" in raw) {
        return raw;
      }
      return { html: raw, fileToPageId: {} };
    }
    const snap = window.__eabMicrositeEditorSnapshot;
    const html2 = snap?.root?.props?.eab_html_preview_srcdoc;
    const zipPath = snap?.root?.props?.eab_html_preview_zip_path;
    return html2 && zipPath ? { html: { [zipPath]: html2 }, fileToPageId: {} } : { html: {}, fileToPageId: {} };
  }
  // POSTs to /ai_enhancements and returns the job_id, or null on error.
  async _submitJob(micrositeId, fileNames, contextHtml) {
    try {
      const res = await fetch(`/microsites/sites/${micrositeId}/ai_enhancements`, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": getCsrfToken()
        },
        credentials: "same-origin",
        body: JSON.stringify({
          ai_enhancement: {
            prompt: this._prompt,
            file_names: fileNames,
            context_html: contextHtml
          }
        })
      });
      const data = await res.json();
      if (!res.ok || data.status !== "processing" || typeof data.job_id !== "string") {
        notifyEnhanceUser({
          message: typeof data.error === "string" ? data.error : `AI request failed (${res.status}).`,
          status: "error"
        });
        return null;
      }
      return data.job_id;
    } catch (err) {
      notifyEnhanceUser({
        message: err instanceof Error ? err.message : "AI request failed.",
        status: "error"
      });
      return null;
    }
  }
  // Waits for the Pusher notification, then fetches the S3 result.
  // Returns the pages map { "file.html": "<html>" } from the backend payload, or null on error.
  _waitForJobResult(jobId, channel) {
    return new Promise((resolve) => {
      const handlers = {};
      let timer;
      const cleanup = () => {
        clearTimeout(timer);
        if (handlers.ready) channel.unbind("microsite_ai_enhancement_ready", handlers.ready);
        if (handlers.error) channel.unbind("microsite_ai_enhancement_error", handlers.error);
      };
      const onReady = async (raw) => {
        const d = raw;
        if (d.job_id !== jobId) return;
        cleanup();
        if (typeof d.url !== "string") {
          notifyEnhanceUser({ message: "Invalid result payload from server.", status: "error" });
          resolve(null);
          return;
        }
        try {
          const s3Res = await fetch(d.url);
          if (!s3Res.ok) {
            notifyEnhanceUser({ message: `Failed to load AI result (${s3Res.status}).`, status: "error" });
            resolve(null);
            return;
          }
          const payload = await s3Res.json();
          const pages = payload.pages;
          if (typeof pages !== "object" || pages === null || Array.isArray(pages)) {
            notifyEnhanceUser({ message: "The AI returned an unexpected response format.", status: "error" });
            resolve(null);
            return;
          }
          resolve(pages);
        } catch (err) {
          notifyEnhanceUser({
            message: err instanceof Error ? err.message : "Failed to load AI result.",
            status: "error"
          });
          resolve(null);
        }
      };
      const onError = (raw) => {
        const d = raw;
        if (d.job_id !== jobId) return;
        cleanup();
        notifyEnhanceUser({
          message: typeof d.error === "string" ? d.error : "AI enhancement failed.",
          status: "error"
        });
        resolve(null);
      };
      handlers.ready = (data) => {
        void onReady(data);
      };
      handlers.error = onError;
      channel.bind("microsite_ai_enhancement_ready", handlers.ready);
      channel.bind("microsite_ai_enhancement_error", handlers.error);
      timer = setTimeout(() => {
        cleanup();
        notifyEnhanceUser({ message: "AI enhancement timed out. Please try again.", status: "error" });
        resolve(null);
      }, JOB_TIMEOUT_MS);
    });
  }
  // ── Render ──────────────────────────────────────────────────────────────────
  render() {
    return html`
      <hi-dialog
        ?opened=${this._opened}
        @close-ended=${this._handleDialogClose}
        wide-screen
        aesthetic="prompt"
      >
        <h2 slot="heading" class="mb-0">Enhance with AI</h2>

        <hi-form
          id=${FORM_ID}
          form=${FORM_ID}
          disable-http
          @submit-ended=${this._handleFormSubmit}
          @cancel-ended=${this._handleFormCancel}
          >
          <hi-text class="eab-html-editor-dialog__description display-block">
            Select pages that you would like to update. Use the AI prompt to describe the changes you would like to apply to the selected pages. For example: “Update all of the font colors to navy blue.”
          </hi-text>

          <hi-input-group>
            <hi-label for="eab-enhance-ai-pages" required>Select Page(s)</hi-label>
            <hi-combobox
              id="eab-enhance-ai-pages"
              name="enhance_page_titles"
              form=${FORM_ID}
              aria-label="Select pages"
              placeholder="Search or select pages"
              cap-height
              required
              ?multiple=${true}
              .input=${this._comboboxInput}
              @commit-ended=${this._handlePageComboboxCommit}
            ></hi-combobox>
          </hi-input-group>
          <div class="eab-html-enhance__full-width-fields">
            <hi-input-group>
              <hi-label for="eab-enhance-ai-prompt" required>
                AI Prompt
                <hi-icon id="eab-enhance-ai-prompt-info" kind="info_circle" theme="blue"></hi-icon>
                <hi-tooltip for="eab-enhance-ai-prompt-info">This feature uses the AI large language model (LLM) configured in the Global Administration settings to generate content based on your prompt. By using this feature, you acknowledge and agree to the use of AI-generated content. Do not enter sensitive, confidential, personal, or proprietary information in your prompt. AI-generated content may be inaccurate or incomplete and should be reviewed before use.</hi-tooltip>
              </hi-label>
              <hi-textarea
                id="eab-enhance-ai-prompt"
                class="eab-html-editor-dialog__text-area"
                name="ai_prompt"
                form=${FORM_ID}
                type="text"
                rows="8"
                placeholder="Example: Change all headings to use Arial font and update the primary color to Green."
                required
                .value=${this._prompt}
                spellcheck="true"
                autocomplete="off"
                @input=${this._handlePromptInput}
              ></hi-textarea>
            </hi-input-group>
          </div>
          <hi-button
            slot="controls"
            type="submit"
            aesthetic="primary"
            ?locked=${this._submitting}
          >
            <span>Save page(s)</span>
          </hi-button>
          <hi-button slot="controls" type="cancel" aesthetic="ghost" ?disabled=${this._submitting}>
            <span>Cancel</span>
          </hi-button>
        </hi-form>
      </hi-dialog>
    `;
  }
}
__decorateClass([
  state()
], MicrositeEnhanceAiDialog.prototype, "_opened", 2);
__decorateClass([
  state()
], MicrositeEnhanceAiDialog.prototype, "_pages", 2);
__decorateClass([
  state()
], MicrositeEnhanceAiDialog.prototype, "_selectedPageTitles", 2);
__decorateClass([
  state()
], MicrositeEnhanceAiDialog.prototype, "_prompt", 2);
__decorateClass([
  state()
], MicrositeEnhanceAiDialog.prototype, "_submitting", 2);
__decorateClass([
  query("#eab-enhance-ai-pages")
], MicrositeEnhanceAiDialog.prototype, "_comboboxEl", 2);
customElements.define(ENHANCE_DIALOG_TAG, MicrositeEnhanceAiDialog);
